import { yupResolver } from "@hookform/resolvers/yup";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import { createEvent, deleteEvent, getEventDetails, updateEvent } from "api/event";
import CustomReactSelect from "components/CustomReactSelect";
import HeaderCard from "components/DataTable/HeaderCard";
import Loading from "components/Loading";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import RideSelector from "components/RideSelector";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useGlobalStore from "store/useGlobalStore";
import Swal from "sweetalert2";
import { getStatusFilters, removeEmptyFields, STATES } from "utils/helper";
import { parentRoute } from ".";

const eventSchema = yup.object({
    ev_name: yup
        .string()
        .trim()
        .required("Event name is required")
        .max(150, "Max 150 characters"),
    ev_start_date: yup
        .string()
        .required("Start date is required"),
    ev_end_date: yup
        .string()
        .required("End date is required")
        .test("end-after-start", "End date cannot be before start date", function (value) {
            const { ev_start_date } = this.parent;
            if (!value || !ev_start_date) return true;
            return new Date(value) >= new Date(ev_start_date);
        }),
    ev_location: yup.string().max(200, "Max 200 characters").nullable(),
    ride_ids: yup
        .array()
        .of(yup.number().typeError("Invalid ride id"))
        .min(1, "Select at least one ride"),
});

const AddEvent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { setPrevSelectedRow } = useGlobalStore();
    const [formData, setFormData] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    const isAdd = useMemo(() =>
        location.pathname.includes(`${parentRoute}/add-event`),
        [location.pathname]
    );

    // React Hook Form: set up with schema resolver (no field name changes)
    const {
        trigger,
        setValue,
        formState: { errors: rhfErrors },
    } = useForm({
        resolver: yupResolver(eventSchema),
        context: { isAdd }, // pass isAdd to schema for ev_status conditional
        mode: "onSubmit",
        reValidateMode: "onChange",
        defaultValues: {
            ev_name: "",
            ev_start_date: "",
            ev_end_date: "",
            ev_location: "",
            ev_state: "",
            ride_ids: [],
            ev_status: undefined,
        },
    });

    const statusSelection = getStatusFilters(false)
    const id = Number(location.pathname.split('/')[2]);

    useEffect(() => {
        const fetchUserData = async () => {
            if (isAdd) return;

            if (!id || isNaN(id)) {
                navigate(parentRoute, { state: location.state });
                return;
            }
            const eventId = id;

            setIsLoading(true);
            try {
                const { data: eventData } = await getEventDetails(eventId);
                if (eventData) {
                    setFormData({
                        id: eventData.id,
                        ev_name: eventData.name,
                        ev_start_date: eventData.start_date,
                        ev_end_date: eventData.end_date,
                        ev_location: eventData.location,
                        ev_state: eventData.state?.id ?? "",
                        ev_status: eventData.status?.id,
                        ride_ids: eventData.rides?.map(ride => ride.id) || []
                    });
                }
            } catch (error) {
                toast.error("Failed to find event data");
                navigate(parentRoute, { state: location.state });
            } finally {
                setIsLoading(false);
            }
        };

        fetchUserData();
    }, [location.pathname, isAdd, navigate]);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    }, []);


    const handleDelete = async () => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "This action cannot be undone.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes'
        });

        if (result.isConfirmed) {
            await deleteEvent(formData.id)
            toast.success("Event deleted successfully");
            navigate(parentRoute);
        }
    }

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        setFormErrors({});
        // Validate that end date is not before start date
        const startDate = formData?.ev_start_date;
        const endDate = formData?.ev_end_date;
        if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
            toast.error("End date cannot be before start date");
            return;
        }

        setIsLoading(true);
        try {
            let passData = removeEmptyFields(formData)
            if (isAdd) {
                await createEvent(passData);
                toast.success("Event added successfully");
                navigate(parentRoute, { state: location.state });
            } else {
                await updateEvent(formData.id, passData);
                toast.success("Event updated successfully");
            }
        } catch (err) {
            setFormErrors(err?.data?.errors || {});
            toast.error(err?.data?.message || (isAdd ? "Failed to create event" : "Failed to update event"));
        } finally {
            setIsLoading(false);
        }
    }, [formData, navigate, isAdd]);

    // Keep RHF values in sync with your existing formData state
    useEffect(() => {
        if (!formData) return;
        setValue("ev_name", formData.ev_name ?? "");
        setValue("ev_start_date", formData.ev_start_date ?? "");
        setValue("ev_end_date", formData.ev_end_date ?? "");
        setValue("ev_location", formData.ev_location ?? "");
        setValue("ev_state", formData.ev_state ?? "");
        setValue("ride_ids", Array.isArray(formData.ride_ids) ? formData.ride_ids : []);
        setValue("ev_status", formData.ev_status ?? undefined);
    }, [formData, setValue]);

    // Mirror RHF errors back to your existing formErrors for display
    useEffect(() => {
        const mapped = {};
        Object.entries(rhfErrors || {}).forEach(([k, v]) => {
            mapped[k] = v?.message;
        });
        setFormErrors(mapped);
    }, [rhfErrors]);

    // Wrapper: validate with RHF/Yup first, then run your original submit logic
    const handleValidatedSubmit = useCallback(
        async (e) => {
            e.preventDefault();
            setFormErrors({});
            const valid = await trigger(); // RHF validates current values
            if (!valid) {
                toast.error("Please fix the validation errors before submitting.");
                return;
            }
            // proceed with your original submit (unchanged)
            await handleSubmit(e);
        },
        [trigger, handleSubmit]
    );

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={4} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card >
                            <HeaderCard
                                title={isAdd ? "Create a New Event" : "Edit Event"}
                                showBackButton={true}
                                onBackClick={() => { navigate(parentRoute, { state: location.state }); setPrevSelectedRow(id, parentRoute) }}
                            />
                            {isLoading && !isAdd ? <Loading /> :
                                <MDBox pt={3} px={2} pb={2}>
                                    {/* Disable native validation so RHF/Yup can handle it */}
                                    <form noValidate onSubmit={handleValidatedSubmit}>
                                        <Grid item xs={12}>
                                            <Grid container spacing={3}>
                                                <Grid item xs={12}>
                                                    <MDInput
                                                        fullWidth
                                                        label="Event name"
                                                        name="ev_name"
                                                        value={formData?.ev_name || ''}
                                                        onChange={handleChange}
                                                        required
                                                        error={!!formErrors?.ev_name}
                                                        helperText={formErrors?.ev_name}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <MDInput
                                                        fullWidth
                                                        label="Start Date"
                                                        name="ev_start_date"
                                                        type="date"
                                                        InputLabelProps={{ shrink: true }}
                                                        value={formData?.ev_start_date || ''}
                                                        onChange={handleChange}
                                                        error={!!formErrors?.ev_start_date}
                                                        helperText={formErrors?.ev_start_date}
                                                        required
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <MDInput
                                                        fullWidth
                                                        label="End Date"
                                                        name="ev_end_date"
                                                        type="date"
                                                        InputLabelProps={{ shrink: true }}
                                                        value={formData?.ev_end_date || ''}
                                                        onChange={handleChange}
                                                        error={!!formErrors?.ev_end_date}
                                                        helperText={formErrors?.ev_end_date}
                                                        required
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <MDInput
                                                        fullWidth
                                                        label="Location"
                                                        name="ev_location"
                                                        value={formData?.ev_location || ''}
                                                        onChange={handleChange}
                                                        error={!!formErrors?.ev_location}
                                                        helperText={formErrors?.ev_location}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <CustomReactSelect
                                                        label="State"
                                                        name="ev_state"
                                                        variant="outlined"
                                                        value={STATES.find(status => status.value === (formData?.ev_state ?? formData?.ev_state?.id))}
                                                        onChange={(selectedOption) => {
                                                            handleChange({ target: { name: 'ev_state', value: selectedOption?.value } });
                                                            // also update RHF for validation
                                                            setValue('ev_state', selectedOption?.value ?? '', { shouldValidate: true });
                                                        }}
                                                        options={STATES}
                                                        required
                                                        error={!!formErrors?.ev_state}
                                                        helperText={formErrors?.ev_state}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <RideSelector
                                                        fullWidth
                                                        label="Ride"
                                                        name="ride_ids"
                                                        value={formData?.ride_ids || ''}
                                                        onChange={(selectedOption) => {
                                                            handleChange({ target: { name: 'ride_ids', value: selectedOption } });
                                                            setValue('ride_ids', selectedOption ?? [], { shouldValidate: true });
                                                        }}
                                                        required
                                                        isMulti
                                                        error={!!formErrors?.ride_ids}
                                                        helperText={formErrors?.ride_ids}
                                                    />
                                                </Grid>

                                                {!isAdd && <Grid item xs={12} md={6}>
                                                    <CustomReactSelect
                                                        label="Status"
                                                        name="ev_status"
                                                        variant="outlined"
                                                        value={statusSelection.find(status => status.value === (formData?.ev_status ?? formData?.ev_status?.id))}
                                                        onChange={(selectedOption) => {
                                                            handleChange({ target: { name: 'ev_status', value: selectedOption?.value } });
                                                            setValue('ev_status', selectedOption?.value ?? '', { shouldValidate: true });
                                                        }}
                                                        options={statusSelection}
                                                        required
                                                        error={!!formErrors?.ev_status}
                                                        helperText={formErrors?.ev_status}
                                                    />
                                                </Grid>}
                                                <Grid item xs={12}>
                                                    <Grid container>
                                                        <Grid item>
                                                            {!isAdd && <MDButton
                                                                variant="contained"
                                                                color="error"
                                                                onClick={handleDelete}
                                                                loading={isLoading}
                                                            >
                                                                Delete
                                                            </MDButton>}
                                                        </Grid>
                                                        <Grid item xs />
                                                        <Grid item>
                                                            <MDBox display="flex" justifyContent="flex-end">
                                                                <MDButton
                                                                    variant="contained"
                                                                    color="primary"
                                                                    type="submit"
                                                                    loading={isLoading}
                                                                >
                                                                    Submit
                                                                </MDButton>
                                                            </MDBox>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </form>
                                </MDBox>}
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
        </DashboardLayout>
    );
};

export default AddEvent;
